import { camelize } from "humps";
import moment from "moment";
import { CustomValuesList } from "PFApp/components/custom_values_list/custom_values_list";
import { Button } from "PFComponents/button";
import { Inline } from "PFComponents/containers";
import { Typography } from "PFComponents/typography";
import { durationFromMonths } from "PFCore/helpers/date";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { CustomType, Profile } from "PFTypes";

import { ExperienceDescription } from "./experience_description";
import css from "./experience_section.module.scss";

type MainJobProps = {
  position: any;
  customTypes: CustomType[];
  profile: Profile;
  currentProfile: Profile;
  isCfPermitted: (cfName: string) => boolean;
  onEdit: () => void;
};

export const MainJob = ({
  customTypes,
  position,
  profile,
  currentProfile,
  isCfPermitted,
  onEdit
}: MainJobProps) => {
  const { formatDate } = useDateFormatter();

  const isMe = !!profile?.id && profile?.id === currentProfile?.id;
  const isOnlyDuration = position.duration && !position.startDate;
  const isStartDateValid = moment(position.startDate).isValid();
  const isEndDateValid = moment(position.endDate).isValid();

  const mainPositionCustomFields = Object.keys(position).filter((item) => isCfPermitted(item));

  const cfName = (name) => customTypes.find((ct) => camelize(ct?.name) === name)?.display_as;

  const cfkeyToValue = mainPositionCustomFields.reduce(
    (acc, item) => ({
      ...acc,
      [item]: cfName(item)
    }),
    {}
  );

  return (
    <div className={css.row}>
      <div className={css.experienceTitle} data-scroll-anchor={`internal-experience-${position.id}`}>
        {position.jobTitle && <Typography variant="h4">{position.jobTitle[0].value}</Typography>}
        {position.company && <Typography variant="h4">{position.company[0].value}</Typography>}
        <Typography variant="h4">
          {isOnlyDuration
            ? durationFromMonths(position.duration)
            : `${isStartDateValid ? formatDate(position.startDate) : ""}${
                isEndDateValid ? ` - ${formatDate(position.endDate)}` : ""
              }`}
        </Typography>
      </div>

      <div className={css.experienceGridWrapper}>
        {(position.summary || isMe) && (
          <Inline gap="spacingLg">
            {position.summary && <ExperienceDescription content={position.summary} maxLength={400} />}
            {isMe && <Button className={css.editIcon} kind="secondary" icon="edit" onClick={onEdit} />}
          </Inline>
        )}
        {mainPositionCustomFields.map(
          (cf) =>
            position[cf] &&
            Array.isArray(position[cf]) && (
              <div key={cf} className={css.experienceGrid}>
                <Typography variant="bodyBold" clipOverflow>
                  {cfkeyToValue[cf]}
                </Typography>
                <div>
                  <CustomValuesList customValues={position[camelize(cf)]} limit={6} />
                </div>
              </div>
            )
        )}
      </div>
    </div>
  );
};
